import {Component, ElementRef, Input, ViewChild} from '@angular/core';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {RepositionTooltipComponent} from '@presentation/components/reposition-tooltip/reposition-tooltip.component';
import {DialogBuilderFactoryService, Message, MessageType, TranslateService} from '@vdw/angular-component-library';
import {isNil} from 'lodash-es';
import {ErrorsAndWarningsDialogComponent} from './errors-and-warnings-dialog/errors-and-warnings-dialog.component';

@Component({
  selector: 'app-errors-and-warnings',
  templateUrl: './errors-and-warnings.component.html',
  styleUrls: ['./errors-and-warnings.component.scss']
})
export class ErrorsAndWarningsComponent {
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public isProductionSchedulePhaseVerify: boolean;
  @Input() public canShowWarningWithText: boolean;
  @ViewChild('errorsAndWarningsButton', {read: ElementRef}) public errorsAndWarningsButton: ElementRef<HTMLElement>;

  public constructor(
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly translate: TranslateService
  ) {}

  public canShowCurrentLossFromProductionSchedule(): boolean {
    return !isNil(this.productionSchedule.warnings?.totalLossInPercent);
  }

  public canShowTotalWidthTooBig(): boolean {
    return this.isProductionSchedulePhaseVerify && !isNil(this.productionSchedule.errors?.totalWidthTooBig);
  }

  public canShowTotalWidthTooLittle(): boolean {
    return this.isProductionSchedulePhaseVerify && !isNil(this.productionSchedule.errors?.totalWidthTooLittle);
  }

  public hasMultipleErrors(): boolean {
    return this.canShowTotalWidthTooBig() && this.canShowTotalWidthTooLittle();
  }

  public hasErrors(): boolean {
    return this.canShowTotalWidthTooBig() || this.canShowTotalWidthTooLittle();
  }

  public hasWarnings(): boolean {
    return this.productionSchedule.isCreelMappingApplied() || this.canShowCurrentLossFromProductionSchedule();
  }

  public hasErrorsAndWarnings(): boolean {
    return this.hasWarnings() && this.hasErrors();
  }

  public canShowAllErrorsAndWarnings(): boolean {
    return this.hasErrorsAndWarnings() || this.hasMultipleErrors();
  }

  public showAllErrorsAndWarnings(event: MouseEvent): void {
    if (this.canShowAllErrorsAndWarnings() || !this.canShowWarningWithText) {
      const messages = this.getWarningAndErrorMessages();
      const errorsAndWarningsButtonDOMRect: DOMRect = this.errorsAndWarningsButton.nativeElement.getBoundingClientRect();

      this.dialogBuilderFactoryService
        .getBuilder()
        .withClass(['reposition-tooltip', 'production-schedule-errors-and-warnings-dialog'])
        .withCustomBackdrop('transparent-backdrop')
        .openDialog(RepositionTooltipComponent, {
          component: ErrorsAndWarningsDialogComponent,
          messages,
          targetDOMRect: {
            top: event.clientY,
            bottom: errorsAndWarningsButtonDOMRect.bottom,
            left: event.clientX,
            right: errorsAndWarningsButtonDOMRect.right
          }
        });
    }
  }

  private getWarningAndErrorMessages(): Message[] {
    const messages = [];

    if (this.canShowCurrentLossFromProductionSchedule()) {
      messages.push(
        new Message(
          null,
          this.translate.instant('PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.CURRENT_LOSS_IN_PERCENT', {currentLossInPercent: this.productionSchedule.warnings.totalLossInPercent}),
          MessageType.WARNING,
          null
        )
      );
    }

    if (this.canShowTotalWidthTooLittle()) {
      messages.push(new Message(null, this.translate.instant('PRODUCTION_ORDER.ERRORS.TOTAL_WIDTH_TOO_LITTLE', {value: this.productionSchedule.errors.totalWidthTooLittle}), MessageType.ERROR, null));
    }

    if (this.canShowTotalWidthTooBig()) {
      messages.push(new Message(null, this.translate.instant('PRODUCTION_ORDER.ERRORS.TOTAL_WIDTH_TOO_BIG', {value: this.productionSchedule.errors.totalWidthTooBig}), MessageType.ERROR, null));
    }

    return messages;
  }
}
